/**
 * Tarjeta individual para mostrar una tarea cron
 * Componente principal que orquesta los subcomponentes
 */
import React, { useMemo, useState } from 'react'
import type { CronTaskConfig } from '../../config/cron/cronTasksConfig'
import type { CronTaskStatusData } from '../../types/cron/cronTasks'
import CronTaskStatus from './CronTaskStatus'
import CronTaskInfo from './CronTaskInfo'
import CronTaskActions from './CronTaskActions'
import EnviaManualProcessModal from './EnviaManualProcessModal'
import HomeCenterManualProcessModal from './HomeCenterManualProcessModal'
import ColvanesManualProcessModal from './ColvanesManualProcessModal'

interface CronTaskCardProps {
  config: CronTaskConfig
  status: CronTaskStatusData | null
  count: number | null
  isLoading: boolean
  error: string | null
  countdown: {
    hours: number
    minutes: number
    seconds: number
    humanReadable: string
    isExpired: boolean
  }
  onExecute: () => Promise<void>
  onManualExecute?: () => void
  isManualLoading?: boolean
  lastSyncedAt?: string | null
  countLabel?: string
}

const CronTaskCard: React.FC<CronTaskCardProps> = React.memo(
  ({
    config,
    status,
    count,
    isLoading: cardLoading,
    error,
    countdown,
    onExecute,
    onManualExecute,
    isManualLoading = false,
    lastSyncedAt,
    countLabel,
  }) => {
    const Icon = config.icon
    const isEnabled = useMemo(() => status?.enabled ?? false, [status?.enabled])

    // Determinar el color del botón según el taskId
    const buttonColor = useMemo(() => {
      if (config.id === 'homecenter-sync-pending') {
        return 'bigcenter' // Azul para HomeCenter
      }
      if (config.id === 'mercadolibre-sync-shipments') {
        return 'mercadol' // Amarillo para MercadoLibre
      }
      if (config.id === 'webhook-retry-failed') {
        return 'red-blue' // Mitad rojo mitad azul para Webhooks
      }
      if (config.id === 'bold-verify-links' || config.id === 'bold-voucher-verify') {
        return 'bold' // Indigo para Bold
      }
      if (config.id === 'envia-hub-sync-guias') {
        return 'colvanes'
      }
      if (config.id === 'flexy-stock-sync') {
        return 'flexy'
      }
      return 'mercadol' // Color por defecto
    }, [config.id])

    const iconWrapperClass =
      config.id === 'envia-hub-sync-guias'
        ? 'p-2 bg-red-50 rounded-lg'
        : config.id === 'flexy-stock-sync'
          ? 'p-2 bg-emerald-50 rounded-lg'
          : 'p-2 bg-blue-50 rounded-lg'
    const iconClass =
      config.id === 'envia-hub-sync-guias'
        ? 'h-6 w-6 text-red-colvanes'
        : config.id === 'flexy-stock-sync'
          ? 'h-6 w-6 text-emerald-600'
          : 'h-6 w-6 text-blue-600'

    return (
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow duration-200">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={iconWrapperClass}>
              <Icon className={iconClass} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {config.name}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {config.description}
              </p>
            </div>
          </div>
        </div>

        {/* Estado */}
        <div className="mb-4">
          <CronTaskStatus status={status} countdown={countdown} />
        </div>

        {/* Información */}
        <div className="mb-4">
          <CronTaskInfo
            status={status}
            count={count}
            showCount={config.showCount ?? false}
            countLabel={countLabel}
            lastSyncedAt={lastSyncedAt}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Acciones */}
        <CronTaskActions
          onExecute={onExecute}
          isLoading={cardLoading}
          isEnabled={isEnabled}
          buttonColor={buttonColor}
          onManualExecute={onManualExecute}
          isManualLoading={isManualLoading}
        />
      </div>
    )
  },
  (prevProps, nextProps) => {
    // Comparador personalizado para evitar re-renders innecesarios
    return (
      prevProps.config.id === nextProps.config.id &&
      prevProps.status?.nextExecutionISO === nextProps.status?.nextExecutionISO &&
      prevProps.count === nextProps.count &&
      prevProps.isLoading === nextProps.isLoading &&
      prevProps.error === nextProps.error &&
      prevProps.countdown.humanReadable === nextProps.countdown.humanReadable
    )
  }
)

CronTaskCard.displayName = 'CronTaskCard'

// Componente wrapper para incluir el modal cuando sea necesario
const CronTaskCardWithModal: React.FC<Omit<CronTaskCardProps, 'onManualExecute' | 'isManualLoading'>> = (props) => {
  const [isManualModalOpen, setIsManualModalOpen] = useState(false)

  // Crear función para abrir el modal manual
  const handleManualExecute = () => {
    setIsManualModalOpen(true)
  }

  // Determinar qué modal usar según el taskId
  const getModal = () => {
    if (props.config.id === 'homecenter-sync-pending') {
      return (
        <HomeCenterManualProcessModal
          isOpen={isManualModalOpen}
          onClose={() => setIsManualModalOpen(false)}
        />
      )
    }
    if (props.config.id === 'webhook-retry-failed') {
      return (
        <EnviaManualProcessModal
          isOpen={isManualModalOpen}
          onClose={() => setIsManualModalOpen(false)}
        />
      )
    }
    if (props.config.id === 'envia-hub-sync-guias') {
      return (
        <ColvanesManualProcessModal
          isOpen={isManualModalOpen}
          onClose={() => setIsManualModalOpen(false)}
        />
      )
    }
    return null
  }

  return (
    <>
      <CronTaskCard
        {...props}
        onManualExecute={props.config.manualExecute ? handleManualExecute : undefined}
        isManualLoading={false}
      />
      {props.config.manualExecute && props.config.id !== 'flexy-stock-sync' && getModal()}
    </>
  )
}

// Exportar el wrapper si tiene manualExecute, sino el componente normal
export { CronTaskCard }

export default function CronTaskCardWrapper(props: Omit<CronTaskCardProps, 'onManualExecute' | 'isManualLoading'>) {
  if (props.config.manualExecute && props.config.id !== 'flexy-stock-sync') {
    return <CronTaskCardWithModal {...props} />
  }
  return <CronTaskCard {...props} />
}

